import { TrainFront, Car, Plane, Footprints, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { useSite, useContent } from "@/context/SiteContext";
import { Container, Eyebrow, LLink, ArrowLink } from "@/components/common";
import { Reveal, LineReveal, Stagger, Item } from "@/components/motion/Reveal";

const ICONS = { walk: Footprints, car: Car, train: TrainFront, plane: Plane };

/** Pin from the hotel's own Google Maps listing (exact coordinates), never geocoded from the street address. */
export function mapsLink(settings) {
  if (settings.maps_url) return settings.maps_url;
  if (settings.maps_lat && settings.maps_lng) return `https://www.google.com/maps/search/?api=1&query=${settings.maps_lat},${settings.maps_lng}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.maps_query || "Hotel Ciutat Martorell")}`;
}

export function MapEmbed({ className = "", height = "100%" }) {
  const { settings } = useSite();
  const q = settings.maps_lat && settings.maps_lng
    ? `${settings.maps_lat},${settings.maps_lng}`
    : encodeURIComponent(settings.maps_query || "Hotel Ciutat Martorell, Av. Pau Claris, 08760 Martorell");
  return (
    <iframe
      title="Mapa Hotel Ciutat Martorell"
      data-testid="map-embed"
      src={`https://www.google.com/maps?q=${q}&output=embed&z=16`}
      className={`w-full border-0 grayscale-[0.4] contrast-[1.05] ${className}`}
      style={{ height }}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      allowFullScreen
    />
  );
}

export function LocationCard() {
  const { t, lang } = useLang();
  const { settings } = useSite();
  const c = useContent();
  const mapsHref = mapsLink(settings);

  return (
    <section data-testid="home-location" className="bg-bone py-20 md:py-32">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow className="mb-6">{t.home.locEyebrow}</Eyebrow></Reveal>
            <LineReveal as="h2" lines={[c("loc_title", lang, t.location.title)]} className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-charcoal" />
            <Reveal delay={0.15}><p className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-charcoal/70">{c("loc_text", lang)}</p></Reveal>
            <Stagger className="mt-10 divide-y divide-line border-y border-line">
              {t.location.pois.slice(0, 5).map((p) => {
                const Icon = ICONS[p.mode] || Car;
                return (
                  <Item key={p.name} className="flex items-center gap-4 py-4">
                    <Icon size={16} strokeWidth={1.25} className="text-terra shrink-0" />
                    <span className="flex-1 text-sm text-charcoal/85">{p.name}</span>
                    <span className="font-serif text-xl text-charcoal">{p.dist}</span>
                    <span className="hidden sm:block w-40 text-right font-mono text-[10px] uppercase tracking-[0.15em] text-charcoal/45">{p.time}</span>
                  </Item>
                );
              })}
            </Stagger>
            <Reveal delay={0.2} className="mt-8 flex flex-wrap items-center gap-6">
              <LLink to="location" data-testid="location-cta"><ArrowLink>{t.home.locCta}</ArrowLink></LLink>
              <a href={mapsHref} target="_blank" rel="noopener noreferrer" data-testid="location-maps-link" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/60 hover:text-charcoal transition-colors">
                {t.location.mapCta} <ArrowUpRight size={12} />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-7 min-h-[420px] lg:min-h-0 bg-stone">
            <MapEmbed height="100%" className="min-h-[420px] h-full" />
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
